import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { ContextService } from '../shared-context/context.service';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulerService } from './scheduler.service';
import { XhrService } from './xhr.service';

export interface TransformsData {
    Transforms: Array<SupportedExtensionsItem>;
}

export interface SupportedExtensionsItem {
    Method: string;
    FileExtensions: Array<string>;
}

@Injectable({
    providedIn: 'root'
})
export class MetaTransformCapacityService extends XhrService {
    private static readonly videoMethod = 'video';
    private capacityDataPromise: Promise<TransformsData>;
    private readonly capacityUrl: string;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp,
            AccessTokenConsumer.None, contextService, officeHomeApiConfigService,
            platformLocation, scheduler);

        this.capacityUrl = `${contextService.sharedContext.metaResourcePath}/transform/capacity`;
    }

    public getCapacityData(): Promise<TransformsData> {
        if (this.capacityDataPromise) {
            return this.capacityDataPromise;
        }

        this.capacityDataPromise = new Promise<TransformsData>((resolve, reject) => {
            const promiseCallback = (promise: InstrumentedHttpPromise<TransformsData>) => {
                promise.instrument((result, event) => {
                    event.params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                })
                .then(({ data }) => resolve(data))
                .catch(error => {
                    this.globalErrorHandler.handleError(error, ErrorId.ouSq3);
                });
            };

            const finalErrorCallback = error => {
                this.capacityDataPromise = null;
                reject(error);
            };

            this.get<TransformsData>(
                KnownInstrumentationEvents.MetaTransformCapacityRequest,
                this.capacityUrl,
                promiseCallback,
                finalErrorCallback,
                {},
                FetchStrategy.NetworkFirst
            );
        });

        return this.capacityDataPromise;
    }

    public getVideoExtensions(): Promise<Array<string>> {
        return this.getCapacityData()
            .then(data => {
                let videoExtensions = null;
                if (data && data.Transforms) {
                    videoExtensions = data.Transforms.find(method => method.Method === MetaTransformCapacityService.videoMethod);
                }

                if (videoExtensions) {
                    return videoExtensions.FileExtensions;
                } else {
                    return [];
                }
            })
            .catch(error => {
                this.globalErrorHandler.handleError(error, ErrorId.ouSq3);
            });
    }
}
